function normalizeFilterResults(response) {
    if (Array.isArray(response)) {
        return response;
    }
    if (response && Array.isArray(response.items)) {
        return response.items;
    }
    if (response && response.data !== undefined) {
        return normalizeFilterResults(response.data);
    }
    return [];
}

export default class Asset {
    constructor(box21_api) {
        this.api = box21_api;
    }

    async filter_options() {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        return this.api.post_with_token('/filter-options', formData)
    }

    async validate(asset_id, bbox_id, model_id) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        formData.append('bbox_id', bbox_id);
        formData.append('model_id', model_id);
        return this.api.post_with_token('/validate-asset', formData)
    }

    async file(asset_id) {
        return this.api.get('/download-asset/' + asset_id)
    }

    async get_crop_for_conf_items(type, items) {
        let formData = new FormData();
        if(type.includes('confused_with_background')) {
            type = 'confused_with_background';
        }
        formData.append('type', type);
        formData.append('items', items);
        return this.api.post_with_token('/get_crop_for_conf_items', formData)
    }

    async get_crop_for_bbox(bbox_id) {
        let formData = new FormData();
        formData.append('bbox_id', bbox_id);
        return this.api.post_with_token('/get_crop_for_bbox', formData)
    }

    async get_crop(url, bbox, asset_id) {
        let formData = new FormData();
        formData.append('url', url);
        formData.append('bbox', bbox);
        formData.append('asset_id', asset_id);
        return this.api.post_with_token('/get_crop', formData)
    }

    async save(asset_id, annotations, in_validation_set) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('asset_id', JSON.stringify(asset_id));
        formData.append('annotations', JSON.stringify(annotations));
        if (in_validation_set !== null && in_validation_set !== undefined) {
            // Always send explicit "true"/"false" strings (FormData boolean coercion is unreliable).
            formData.append('in_validation_set', in_validation_set ? 'true' : 'false');
        }
        return this.api.post_with_token('/store', formData)
    }

    async add_to_validation_set(asset_ids) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('asset_ids', JSON.stringify(asset_ids));
        return this.api.post_with_token('/add-to-validation-set', formData)
    }

    async add_filter_to_validation_set(filters) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('filters', JSON.stringify(filters));
        return this.api.post_with_token('/add-filters-to-validation-set', formData)
    }

    async remove_from_validation_set(asset_ids) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('asset_ids', JSON.stringify(asset_ids));
        return this.api.post_with_token('/remove-from-validation-set', formData)
    }

    async delete(asset_ids) {
        let formData = new FormData();
        formData.append('asset_ids', JSON.stringify(asset_ids));
        return this.api.post_with_token('/assets/delete', formData)
    }

    /**
     * Add a full-image bounding box ([0,0,1,1]) with labelName to each asset.
     * @param {number[]} asset_ids
     * @param {string} label_name
     */
    async add_full_image_bboxes(asset_ids, label_name) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('asset_ids', JSON.stringify(asset_ids));
        formData.append('label_name', label_name);
        return this.api.post_with_token('/assets/add-full-image-bboxes', formData)
    }

    async addFullImageBboxesInBatches(asset_ids, label_name, onProgress, batchSize = 50) {
        return this.processAssetIdsInBatches(
            asset_ids,
            (batch) => this.add_full_image_bboxes(batch, label_name),
            onProgress,
            batchSize,
        );
    }

    async processAssetIdsInBatches(asset_ids, batchFn, onProgress, batchSize = 10) {
        const ids = Array.isArray(asset_ids) ? asset_ids : [];
        if (ids.length === 0) {
            if (onProgress) {
                onProgress({ percent: 100, processed: 0, total: 0 });
            }
            return;
        }
        const total = ids.length;
        let processed = 0;
        for (let i = 0; i < total; i += batchSize) {
            const batch = ids.slice(i, i + batchSize);
            await batchFn(batch);
            processed += batch.length;
            if (onProgress) {
                onProgress({
                    percent: Math.round((processed / total) * 100),
                    processed,
                    total,
                });
            }
        }
    }

    async deleteInBatches(asset_ids, onProgress, batchSize = 10) {
        return this.processAssetIdsInBatches(
            asset_ids,
            (batch) => this.delete(batch),
            onProgress,
            batchSize,
        );
    }

    async addToValidationSetInBatches(asset_ids, onProgress, batchSize = 50) {
        return this.processAssetIdsInBatches(
            asset_ids,
            (batch) => this.add_to_validation_set(batch),
            onProgress,
            batchSize,
        );
    }

    async removeFromValidationSetInBatches(asset_ids, onProgress, batchSize = 50) {
        return this.processAssetIdsInBatches(
            asset_ids,
            (batch) => this.remove_from_validation_set(batch),
            onProgress,
            batchSize,
        );
    }

    async delete_assets_in_filter(filters) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('filters', JSON.stringify(filters));
        return this.api.post_with_token('/assets/delete-in-filter', formData)
    }

    async analyse_selected(params) {
        let formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('asset_ids', JSON.stringify(params.asset_ids));
        formData.append('model_id', params.model.id);
        if(params.crop_on) {
            formData.append('crop_on', params.crop_on);
        }
        if(params.include_descendents !== undefined) {
            formData.append('include_descendents', params.include_descendents);
        }
        return this.api.post_with_token('/analyse-assets', formData)
    }

    async analyse_with_filter(params) {
        let model = params.model;
        let filter = params.filter;
        // FormData stringifies undefined as "undefined", which the API treats as an
        // explicit false (not the empty default). Always send real booleans.
        let clear_existing = params.clear_existing === true || params.clear_existing === 'true';
        let clear_existing_model = params.clear_existing_model !== false && params.clear_existing_model !== 'false';
        let include_descendents = params.include_descendents;

        let formData = new FormData();
        formData.append('filter', JSON.stringify(filter));
        formData.append('model_id', model.id);
        if(params.crop_on) {
            formData.append('crop_on', params.crop_on);
        }
        if(params.limit) {
            formData.append('limit', params.limit);
        }

        formData.append('clear_existing', clear_existing);
        if (include_descendents !== undefined) {
            formData.append('include_descendents', include_descendents);
        }
        formData.append('clear_existing_model', clear_existing_model);
        formData.append('project_id', sessionStorage.getItem('project_id'));
        return this.api.post_with_token('/analyse-assets-for-filter', formData)
    }

    async llmAnalyseStart(params) {
        const formData = new FormData();
        formData.append('project_id', sessionStorage.getItem('project_id'));
        if (params.category_label_id != null) {
            formData.append('category_label_id', String(params.category_label_id));
        }
        formData.append('find_query', params.find_query != null ? String(params.find_query) : '');
        formData.append('do_find', params.do_find ? 'true' : 'false');
        formData.append('do_verify', params.do_verify ? 'true' : 'false');
        formData.append('do_llm_question', params.do_llm_question ? 'true' : 'false');
        formData.append('llm_question', params.llm_question != null ? String(params.llm_question) : '');
        formData.append(
            'llm_question_meta_key',
            params.llm_question_meta_key != null ? String(params.llm_question_meta_key) : '',
        );
        formData.append('do_llm_prompt_boxes', params.do_llm_prompt_boxes ? 'true' : 'false');
        formData.append(
            'llm_prompt_boxes_text',
            params.llm_prompt_boxes_text != null ? String(params.llm_prompt_boxes_text) : '',
        );
        if (params.llm_prompt_boxes_label_id != null && params.llm_prompt_boxes_label_id !== '') {
            formData.append('llm_prompt_boxes_label_id', String(params.llm_prompt_boxes_label_id));
        }

        const assetIds = (params.asset_ids || [])
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id));
        if (assetIds.length > 0) {
            formData.append('asset_ids', JSON.stringify(assetIds));
        } else if (params.filter) {
            formData.append('filter', JSON.stringify(params.filter));
        }

        return this.api.post_with_token('/auto/llm-analyse', formData);
    }

    async filter(filter, limit, options = {}) {
        let formData = new FormData();
        formData.append('filters', JSON.stringify(filter));
        formData.append('limit', limit);
        formData.append('project_id', sessionStorage.getItem('project_id'));
        formData.append('random_offset', true);
        if (options.include_count) {
            formData.append('include_count', 'true');
        }
        const response = await this.api.post_with_token('/assets/filter/v2', formData);
        if (options.include_count && response && typeof response === 'object' && Array.isArray(response.items)) {
            return { items: normalizeFilterResults(response.items), count: response.count };
        }
        return normalizeFilterResults(response);
    }

    async count_filter(filter) {
        let formData = new FormData();
        formData.append('filters', JSON.stringify(filter));
        formData.append('project_id', sessionStorage.getItem('project_id'));
        return this.api.post_with_token('/assets/filter/v2/count', formData)
    }

    async number_of_validation_images_for_train_config(train_config_id) {
        let formData = new FormData();
        formData.append('train_config_id', train_config_id);
        formData.append('project_id', sessionStorage.getItem('project_id'));
        return this.api.post_with_token('/assets/number-of-validation-images-for-train-config', formData)
    }

    async mark_invalidated(asset_ids) {
        let formData = new FormData();
        formData.append('asset_ids', JSON.stringify(asset_ids));
        return this.api.post_with_token('/assets/invalidate', formData)
    }

    async mark_unclear(asset_id) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        return this.api.post_with_token('/assets/mark-unclear', formData)
    }

    async mark_skipped(asset_id) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        return this.api.post_with_token('/assets/mark-skipped', formData)
    }

    async load(asset_id) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        return this.api.post_with_token('/assets/load', formData)
    }

    async addAssetMessage(asset_id, body_markdown) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        formData.append('body_markdown', body_markdown);
        return this.api.post_with_token('/asset-messages/add', formData);
    }

    async archiveAssetMessage(asset_id, message_id) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        formData.append('message_id', String(message_id));
        return this.api.post_with_token('/asset-messages/archive', formData);
    }

    async get(asset_id) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        return this.api.post_with_token('/asset', formData)
    }

    async mark_validated(asset_ids) {
        let formData = new FormData();
        formData.append('asset_ids', JSON.stringify(asset_ids));
        return this.api.post_with_token('/assets/validate', formData)
    }

    async mark_filter_validated(filters) {
        let formData = new FormData();
        formData.append('filters', JSON.stringify(filters));
        formData.append('project_id', sessionStorage.getItem('project_id'));
        return this.api.post_with_token('/assets/validate-filters', formData)
    }
    
    async accept_suggestions(asset_id, suggestion_ids) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        formData.append('suggestion_ids', JSON.stringify(suggestion_ids));
        return this.api.post_with_token('/asset/apply-suggestions', formData)
    }

    async discard_all_suggestions(asset_id) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        return this.api.post_with_token('/asset/discard-all-suggestions', formData)
    }

    async next_asset(filters, offset, excludeAssetIds = []) {
        let formData = new FormData();
        if (offset != null && offset !== undefined) {
            formData.append('offset', offset);
        }
        formData.append('project_id', sessionStorage.getItem('project_id'));
        const exclude = Array.isArray(excludeAssetIds)
            ? excludeAssetIds.filter((id) => id != null && id !== '')
            : [];
        if (exclude.length > 0) {
            formData.append('exclude_asset_ids', JSON.stringify(exclude));
            formData.append('limit', '20');
        } else {
            formData.append('limit', '1');
        }
        formData.append('filters', filters);
        return this.api.post_with_token('/next', formData)
    }

    async update_meta_key(asset_id, key, value) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        formData.append('key', key);
        formData.append('value', value);
        return this.api.post_with_token('/asset/meta/update-value', formData)
    }

    async delete_meta_key(asset_id, key) {
        let formData = new FormData();
        formData.append('asset_id', asset_id);
        formData.append('key', key);
        return this.api.post_with_token('/asset/meta/delete-key', formData)
    }


}